import React, { useRef, useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { apiGet } from "../api";
import { invoicePdfTitle } from "../utils/invoiceNumber";
import InvoiceDocument from "./invoices/InvoiceDocument";
import useInvoiceFontSize from "./invoices/useInvoiceFontSize";
import PageBreakChoice, {
  measurePrintLayout,
  wouldOverflow,
  pagesNeeded,
  fitScale,
  printSheetCss,
} from "./invoices/PageBreakChoice";

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const printedRef = useRef(false);
  const [invoice, setInvoice] = useState(null);
  const invoiceFontSize = useInvoiceFontSize();
  // "fit" squeezes the invoice onto one sheet (the default, and right for one a
  // line or two over). "flow" lets it run on. The choice is only ever asked
  // when the invoice genuinely does not fit.
  const [pageMode, setPageMode] = useState("fit");
  // How far "fit" shrinks the document to get it onto the sheet; 1 when it
  // already fits. Worked out at print time, from the sheet, not the screen.
  const [fitZoom, setFitZoom] = useState(1);
  const [printChoice, setPrintChoice] = useState(null); // { pages } while asking
  const documentRef = useRef(null);

  // ---- Load invoice once ----
  useEffect(() => {
    async function load() {
      try {
        const data = await apiGet(`/invoices/${id}/`);
        
        // Use database values directly - these are already correct
        const subtotal = parseFloat(data.subtotal || 0);
        const discount = parseFloat(data.discount_value || 0);
        const disbursements = parseFloat(data.disbursements || 0);
        const gst = parseFloat(data.gst || 0);
        const pst = parseFloat(data.pst || 0);
        const hst = parseFloat(data.hst || 0);
        const total = parseFloat(data.total || 0);

        setInvoice({
          ...data,
          subtotal,
          discount,
          disbursements,
          tax_gst: gst,
          tax_pst: pst,
          tax_hst: hst,
          total_amount: total,
        });
      } catch (e) {
        console.error("Failed to load invoice:", e);
      }
    }
    load();
  }, [id]);

  // Prints this page directly rather than writing a second window. The old
  // popup approach cannot be reached from the invoices list -- window.open is
  // only reliably allowed during a user gesture, and the auto-print path fires
  // from an effect. printSheetCss (rendered below) isolates #invoice-content.
  const handlePrint = () => {
    const layout = measurePrintLayout(documentRef.current);
    if (wouldOverflow(layout)) {
      // Ask before printing, not after: the alternative is the client
      // receiving whichever the layout happened to pick.
      setPrintChoice({ pages: pagesNeeded(layout), scale: fitScale(layout) });
      return;
    }
    // Within a few pixels of the sheet still gets the small shrink it needs:
    // one pixel over is a blank second sheet.
    const scale = fitScale(layout);
    if (pageMode === "fit" && fitZoom === scale) {
      window.print();
      return;
    }
    setPageMode("fit");
    setFitZoom(scale);
    printAfterRender();
  };

  // Applying a mode is a render away, so print the frame that carries it.
  // requestAnimationFrame rather than a timeout: print the frame that
  // actually has the new layout in it.
  const printAfterRender = () => {
    const run = typeof requestAnimationFrame === "function"
      ? requestAnimationFrame
      : (fn) => setTimeout(fn, 0);
    run(() => run(() => window.print()));
  };

  const choosePageMode = (mode) => {
    const scale = printChoice?.scale || 1;
    setPrintChoice(null);
    setPageMode(mode);
    setFitZoom(mode === "fit" ? scale : 1);
    printAfterRender();
  };

  // Auto-print when opened from the invoices list's Print action.
  //
  // Waits for `invoice`: the page renders "Loading…" until it arrives, and
  // printing that produces a blank page. The ref makes it fire once -- a
  // re-render must not reopen the dialog.
  useEffect(() => {
    if (!invoice) return;
    if (searchParams.get("print") !== "1") return;
    if (printedRef.current) return;
    printedRef.current = true;
    window.onafterprint = () => window.close();
    handlePrint();
    return () => {
      window.onafterprint = null;
    };
  }, [invoice, searchParams]);

  // Chrome's Save-as-PDF defaults the filename to the document title, which
  // otherwise stays "ClientZone" (public/index.html) for every invoice.
  useEffect(() => {
    if (!invoice) return undefined;
    const previous = document.title;
    document.title = invoicePdfTitle({
      clientName: invoice.client_name,
      number: invoice.number,
      date: invoice.date,
    });
    return () => {
      document.title = previous;
    };
  }, [invoice]);

  const handleEdit = () => navigate(`/invoices/create?id=${invoice?.id}`);

  // ---- Guard until data ready ----
  if (!invoice) {
    return <div style={{ padding: 20 }}>Loading…</div>;
  }

  // ---- Render ----
  return (
    <div style={{ padding: 20 }}>
      {/* The sheet: this invoice alone, one letter page tall (or running on,
          in "flow"). See printSheetCss for why it is display:none and 100%
          rather than visibility:hidden and 100vh. */}
      <style>{printSheetCss("#invoice-content", { mode: pageMode, scale: fitZoom })}</style>
      <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center" }}>
        {/* Straight to the list, not history.back(): the invoice may have been
            opened from the Create page, or from a link with no history at all,
            and "back" would then lead somewhere unhelpful or out of the app. */}
        <button
          onClick={() => navigate("/invoices")}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: "1px solid #d1d5db",
            background: "white",
            cursor: "pointer",
            fontSize: 13,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span aria-hidden="true">←</span> Back to Invoices
        </button>

        <button
          onClick={handlePrint}
          style={{
            padding: "8px 16px",
            borderRadius: 6,
            border: "1px solid #d1d5db",
            background: "#f9fafb",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          Print to PDF
        </button>

        <button
          onClick={handleEdit}
          style={{
            padding: "8px 16px",
            borderRadius: 6,
            border: "1px solid #ef4444",
            background: "#fff5f5",
            color: "#b91c1c",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Edit Invoice
        </button>
      </div>

      {/* ✅ Printable region */}
      <div
        id="invoice-content"
        ref={documentRef}
        data-page-mode={pageMode}
        style={{
          fontFamily: "'Times New Roman', Times, serif",
          fontSize: 13,
          padding: "50px 70px",
          color: "#000",
          lineHeight: 1.5,
          maxWidth: 800,
          margin: "0 auto",
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          minHeight: "100vh",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <InvoiceDocument
          fontSize={invoiceFontSize}
          invoice={invoice}
          client={{
            street_address: invoice.client_street_address,
            city: invoice.client_city,
            province: invoice.client_province,
            postal_code: invoice.client_postal_code,
            bill_to: invoice.client_bill_to,
            name: invoice.client_name,
          }}
        />
      </div>

      <PageBreakChoice
        open={Boolean(printChoice)}
        pagesIfFlowed={printChoice?.pages}
        onChoose={choosePageMode}
        onCancel={() => setPrintChoice(null)}
      />
    </div>
  );
}